import React from "react";
import { Menu, Github, Linkedin, Mail, ExternalLink } from "lucide-react";
import coverIcon from "../images/suman.jpg";
import { Highlight } from "./Highlight";
import { Feedback } from "./feedback.js"

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const projects = [
    {
      title: "Delv",
      description: "A commercial website for company with products & services with simple layout and designs.",
      tags: ["JavaScript","HTML/CSS", "PHP"],
      link: "https://delv.au/",
    },
    {
      title: "The Nod App",
      description: "WebApp for Realtime recognition, tipping, and appreciation.",
      tags: ["React", "JavaScript", "Tailwind CSS", "Figma"],
      link: "https://thenod.app/",
    },
    {
      title: "TestApp.io",
      description: "Mobile-responsive dashboard application",
      tags: ["React", "Tailwind", "Chart.js"],
      link: "https://testapp.io/",
    },
  ];

  const skills = [
    "JavaScript",
    "React",
    "Node.js",
    "HTML/CSS",
    "Tailwind CSS",
    "Git",
    "MongoDB",
    "REST APIs",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img
                className="h-10 w-10 rounded-full"
                alt="profilw"
                src={coverIcon}
              />
              <span>Suman Katwal</span>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Desktop menu */}
            <div className="hidden md:flex space-x-8">
              <a href="#about" className="text-gray-700 hover:text-gray-900">
                About
              </a>
              <a href="#projects" className="text-gray-700 hover:text-gray-900">
                Projects
              </a>
              <a href="#skills" className="text-gray-700 hover:text-gray-900">
                Skill
              </a>
              <a href="#contact" className="text-gray-700 hover:text-gray-900">
                Contact
              </a>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden pb-4">
              <a href="#about" className="block py-2 text-gray-700">
                ABOUT
              </a>
              <a href="#projects" className="block py-2 text-gray-700">
                PROJECTS
              </a>
              <a href="#skills" className="block py-2 text-gray-700">
                SKILLS
              </a>
              <a href="#contact" className="block py-2 text-gray-700">
                CONTACT
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-200 to-blue-300">
        <div className="max-w-6xl mx-auto px-4 py-20">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Hi, I'm Suman Katwal 
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8">
            A creative web designer specializing in responsive, user-friendly websites with modern design, intuitive UX/UI, and performance-driven solutions.
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="#contact"
              className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Contact Me
            </a>
            <a
              href="#projects"
              className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              View Projects
            </a>
            <a
              href="https://drive.google.com/file/d/10u12DUUhFBtiEwbYzGpb914BdoAn2A08/view?usp=drive_link" target="_blank"
              className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Download CV
            </a>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12">Projects</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                <p className="text-gray-600 mb-4">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="bg-gray-100 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href={project.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700"
                >
                  View Project <ExternalLink className="ml-1 h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Projects Section */}
      <section className="text-left">
        <Highlight />
      </section>

     <section className="my-3 bg-blue-200">
        <Feedback/>
        </section>

      {/* Skills Section */}
      <section id="skills" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12">Skills</h2>
          <div className="flex justify-center flex-wrap gap-4">
            {skills.map((skill, index) => (
              <span
                key={index}
                className="border border-green-500 px-4 py-2 rounded-lg text-indigo-800"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12">Get In Touch</h2>
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
            <a
              href="https://github.com/suman-katwal"
              className="flex items-center text-gray-700 hover:text-gray-900"
            >
              <Github className="h-6 w-6 mr-2" /> GitHub
            </a>
            <a
              href="https://www.linkedin.com/in/suman-katwal/"
              className="flex items-center text-gray-700 hover:text-gray-900"
            >
              <Linkedin className="h-6 w-6 mr-2" /> LinkedIn
            </a>
            <a
              href="mailto:katwalsuman12@gmail.com"
              className="flex items-center text-gray-700 hover:text-gray-900"
            >
              <Mail className="h-6 w-6 mr-2" /> Email
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <p className="text-center text-gray-600">
            © 2024 Suman Katwal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;
